if (!customElements.get('dusk-dream-video')) {
  customElements.define('dusk-dream-video', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.video = this.querySelector('video');
      this.abort = new AbortController();
      this.mobile = matchMedia('(max-width: 749px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.near = false;
      this.visible = false;
      this.loadedUrl = '';
      this.mediaReady = false;
      this.generation = 0;
      this.playSerial = 0;
      const signal = this.abort.signal;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.video.loop = true;
      this.video.controls = false;
      this.video.autoplay = false;
      const mediaReady = () => {
        if (!this.ready || !this.loadedUrl) return;
        if (this.video.currentSrc && this.video.currentSrc !== this.loadedUrl) return;
        this.mediaReady = true;
        if (this.motionAllowed()) this.setAttribute('data-video-ready', '');
        this.updatePlayback();
      };
      this.video.addEventListener('loadeddata', mediaReady, { signal });
      this.video.addEventListener('canplay', mediaReady, { signal });
      this.video.addEventListener('playing', () => {
        if (!this.visible || document.hidden || !this.motionAllowed()) {
          this.video.pause();
          return;
        }
        this.setAttribute('data-video-playing', '');
      }, { signal });
      this.video.addEventListener('pause', () => this.showPaused(), { signal });
      this.video.addEventListener('error', () => {
        this.mediaReady = false;
        this.removeAttribute('data-video-ready');
        this.showPaused();
      }, { signal });
      this.mobile.addEventListener('change', () => this.changeMedia(), { signal });
      this.reduced.addEventListener('change', () => this.updatePlayback(), { signal });
      this.connection?.addEventListener?.('change', () => this.updatePlayback(), { signal });
      document.addEventListener('visibilitychange', () => this.updatePlayback(), { signal });
      this.nearObserver = new IntersectionObserver(entries => {
        this.near = entries[0].isIntersecting;
        if (this.near && this.motionAllowed()) this.loadVideo();
      }, { rootMargin: '350px 0px' });
      this.visibleObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.updatePlayback();
      }, { threshold: 0 });
      this.nearObserver.observe(this);
      this.visibleObserver.observe(this.querySelector('.dusk-dream__media'));
      this.showPaused();
    }
    motionAllowed() {
      return !this.reduced.matches && !this.connection?.saveData;
    }
    loadVideo() {
      if (!this.ready || !this.motionAllowed()) return;
      const kind = this.mobile.matches ? 'mobile' : 'desktop';
      const url = this.dataset[kind + 'Video'];
      if (!url || this.loadedUrl === url) return;
      this.loadedUrl = url;
      this.mediaReady = false;
      this.removeAttribute('data-video-ready');
      this.video.poster = this.dataset[kind + 'Poster'];
      this.video.src = url;
      this.video.load();
    }
    updatePlayback() {
      if (!this.ready) return;
      if (!this.motionAllowed()) this.removeAttribute('data-video-ready');
      if (!this.visible || document.hidden || !this.motionAllowed()) {
        this.video.pause();
        this.showPaused();
        return;
      }
      this.loadVideo();
      if (this.mediaReady) this.setAttribute('data-video-ready', '');
      if (!this.loadedUrl || !this.mediaReady || this.video.readyState < 2 || !this.video.paused) return;
      if (this.playPromise) { this.playRetry = true; return; }
      const generation = this.generation;
      const serial = ++this.playSerial;
      const playback = this.video.play();
      if (playback?.then) {
        this.playPromise = playback;
        playback.catch(() => {
          if (generation === this.generation && serial === this.playSerial) this.showPaused();
        }).finally(() => {
          if (generation !== this.generation || serial !== this.playSerial || this.playPromise !== playback) return;
          this.playPromise = null;
          if (this.playRetry) { this.playRetry = false; this.updatePlayback(); }
        });
      }
    }
    showPaused() {
      this.removeAttribute('data-video-playing');
    }
    changeMedia() {
      if (!this.ready) return;
      this.generation++;
      this.playSerial++;
      this.video.pause();
      this.playPromise = null;
      this.playRetry = false;
      this.loadedUrl = '';
      this.mediaReady = false;
      this.removeAttribute('data-video-ready');
      this.video.removeAttribute('src');
      this.video.load();
      if (this.near && this.motionAllowed()) this.loadVideo();
      this.updatePlayback();
    }
    disconnectedCallback() {
      this.ready = false;
      this.generation++;
      this.playSerial++;
      this.abort?.abort();
      this.nearObserver?.disconnect();
      this.visibleObserver?.disconnect();
      this.video?.pause();
      this.video?.removeAttribute('src');
      this.video?.load();
      this.playPromise = null;
    }
  });
}
